import React from "react";
import { FastField, FieldArray, Field } from "formik";
import { Output } from "../../Text";
import { Button, Fieldset, Form, Layout } from "@vwfs-bronson/bronson-react";
import {
  Conditional,
  FieldDependent,
  renderRadioGroup,
  SelectField,
  TextField,
} from "../../formik";
import * as Validation from "../../validation";
import {
  showMonthlyRateProperty,
  showMonthlyRateRent,
} from "../../functional/expenditure";
import toOptions from "../../functional/toOptions";
import { FormSection2 } from "./FormSection";
import { DateField } from "../../formik/DateField";
import { resetField } from "../../formik/resetField";
import { normalizeEuroAmount } from "../../functional/normalize";

function ExpenditurePanel({
  closed,
  success,
  showEditButton,
  onIconClick,
  form,
  buttons,
  containerRef,
}) {
  const currencyAddon = {
    addonText: Output.resolve("generic.currencySymbol"),
  };

  return (
    <FormSection2
      containerRef={containerRef}
      title={Output.resolve("pages.applicationForm.panels.expenditure.title")}
      subtitle={Output.resolve(
        "pages.applicationForm.panels.expenditure.subtitle"
      )}
      success={success}
      expanded={!closed}
      onHeaderClick={onIconClick}
      edit={
        !showEditButton
          ? undefined
          : {
              label: Output.resolve("pages.applicationForm.buttonEdit"),
              onClick: onIconClick,
            }
      }
    >
      <Form {...form} floatingLabel>
        <Fieldset>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <Field
                  name="netIncome"
                  label={Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.netIncome.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.expenditure.fields.netIncome.toolTip"
                  )}
                  normalizeOnChange={false}
                  normalize={normalizeEuroAmount}
                  reversed
                  {...currencyAddon}
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="additionalNetIncome"
                  normalizeOnChange={false}
                  normalize={normalizeEuroAmount}
                  reversed
                  {...currencyAddon}
                  label={Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.additionalNetIncome.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.expenditure.fields.additionalNetIncome.toolTip"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "livingConditions",
                  legend: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.livingConditions.label"
                  ),
                  options: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.livingConditions.values",
                    []
                  ),
                })}
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FieldDependent
                  names={["livingConditions"]}
                  render={({ values: { livingConditions }, formik }) => (
                    <Conditional
                      collapsed={livingConditions !== "PROPERTY_OWNERSHIP"}
                      onCollapse={() => {
                        resetField("selfUsedProperty", formik);
                      }}
                    >
                      {renderRadioGroup({
                        fieldName: "selfUsedProperty",
                        legend: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.selfUsedProperty.label"
                        ),
                        options: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.selfUsedProperty.values",
                          []
                        ),
                      })}
                    </Conditional>
                  )}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <FieldDependent
                names={["livingConditions", "selfUsedProperty"]}
                render={({
                  values: { livingConditions, selfUsedProperty },
                  formik,
                }) => (
                  <>
                    <Conditional
                      collapsed={
                        !showMonthlyRateRent({
                          livingConditions,
                          selfUsedProperty,
                        })
                      }
                      onCollapse={() => {
                        resetField("monthlyRateLease", formik);
                      }}
                    >
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="monthlyRateLease"
                          normalizeOnChange={false}
                          normalize={normalizeEuroAmount}
                          reversed
                          {...currencyAddon}
                          label={Output.resolve(
                            "pages.applicationForm.panels.expenditure.fields.monthlyRateLease.label"
                          )}
                          tooltip={Output.resolveAsMarkdown(
                            "pages.applicationForm.panels.expenditure.fields.monthlyRateLease.toolTip"
                          )}
                          component={TextField}
                        />
                      </Layout.Item>
                    </Conditional>

                    <Conditional
                      collapsed={
                        !showMonthlyRateProperty({
                          livingConditions,
                          selfUsedProperty,
                        })
                      }
                      onCollapse={() => {
                        resetField("monthlyRateProperty", formik);
                      }}
                    >
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="monthlyRateProperty"
                          normalizeOnChange={false}
                          normalize={normalizeEuroAmount}
                          reversed
                          {...currencyAddon}
                          label={Output.resolve(
                            "pages.applicationForm.panels.expenditure.fields.monthlyRateProperty.label"
                          )}
                          component={TextField}
                        />
                      </Layout.Item>
                    </Conditional>
                  </>
                )}
              />
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="extraCost"
                  normalizeOnChange={false}
                  normalize={normalizeEuroAmount}
                  reversed
                  {...currencyAddon}
                  label={Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.extraCost.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.expenditure.fields.extraCost.toolTip"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="alimentChildrenCount"
                  label={Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.alimentChildrenCount.label"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "otherObligationAlignment",
                  legend: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.otherObligationAlignment.label"
                  ),
                  tooltip: Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.expenditure.fields.otherObligationAlignment.toolTip"
                  ),
                  options: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.otherObligationAlignment.values",
                    []
                  ),
                })}
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FieldDependent
                  names={["otherObligationAlignment"]}
                  render={({
                    values: { otherObligationAlignment },
                    formik,
                  }) => (
                    <Conditional
                      collapsed={otherObligationAlignment !== "yes"}
                      onCollapse={() => {
                        resetField("monthlyAlignmentFees", formik);
                      }}
                    >
                      <FastField
                        name="monthlyAlignmentFees"
                        normalizeOnChange={false}
                        normalize={normalizeEuroAmount}
                        reversed
                        {...currencyAddon}
                        label={Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.monthlyAlignmentFees.label"
                        )}
                        tooltip={Output.resolveAsMarkdown(
                          "pages.applicationForm.panels.expenditure.fields.monthlyAlignmentFees.toolTip"
                        )}
                        component={TextField}
                      />
                    </Conditional>
                  )}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "insuranceType",
                  legend: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.insuranceType.label"
                  ),
                  tooltip: Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.expenditure.fields.insuranceType.toolTip"
                  ),
                  options: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.insuranceType.values",
                    []
                  ),
                })}
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FieldDependent
                  names={["insuranceType"]}
                  render={({ values: { insuranceType }, formik }) => (
                    <Conditional
                      collapsed={insuranceType !== "private"}
                      onCollapse={() => {
                        resetField("monthlyFeeInsurance", formik);
                      }}
                    >
                      <FastField
                        name="monthlyFeeInsurance"
                        normalizeOnChange={false}
                        normalize={normalizeEuroAmount}
                        reversed
                        {...currencyAddon}
                        label={Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.monthlyFeeInsurance.label"
                        )}
                        tooltip={Output.resolveAsMarkdown(
                          "pages.applicationForm.panels.expenditure.fields.monthlyFeeInsurance.toolTip"
                        )}
                        component={TextField}
                      />
                    </Conditional>
                  )}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>{" "}
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "activeCreditContracts",
                  legend: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.activeCreditContracts.label"
                  ),
                  options: Output.resolve(
                    "pages.applicationForm.panels.expenditure.fields.activeCreditContracts.values",
                    []
                  ),
                })}
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1" />
            </Layout>
          </Fieldset.Row>
          <FieldDependent
            names={["activeCreditContracts"]}
            render={({ values: { activeCreditContracts }, formik }) => {
              const createCredit = () => ({
                creditType: undefined,
                creditMonthlyRate: undefined,
                creditBegin: undefined,
                creditTerm: undefined,
              });

              return (
                <Conditional
                  collapsed={activeCreditContracts !== "yes"}
                  onCollapse={() => {
                    resetField("credits", formik, []);
                  }}
                  onShow={() => {
                    formik.setFieldValue("credits", [createCredit()]);
                  }}
                >
                  <FieldArray
                    name="credits"
                    render={({ form, name, ...arrayHelpers }) => {
                      const credits = form.values[name];
                      const creditTypeLocalization = Output.resolve(
                        "pages.applicationForm.panels.expenditure.fields.credits.type.values",
                        []
                      );

                      return (
                        <>
                          {credits.map((c, index) => {
                            const names = [
                              "creditType",
                              "creditMonthlyRate",
                              "creditBegin",
                              "creditTerm",
                            ].reduce((acc, v) => {
                              acc[v] = `${name}[${index}].${v}`;
                              return acc;
                            }, {});

                            return (
                              <React.Fragment key={index}>
                                <Fieldset.Row>
                                  <Layout key={index}>
                                    <Layout.Item
                                      default="6/12"
                                      m="1/2"
                                      xs="1/1"
                                    >
                                      <FastField
                                        name={names.creditType}
                                        label={Output.resolve(
                                          "pages.applicationForm.panels.expenditure.fields.credits.type.label"
                                        )}
                                        component={SelectField}
                                      >
                                        {[
                                          "",
                                          "CONSUMER_CREDIT",
                                          "LEASING",
                                          "FRAMEWORK_CREDIT",
                                          "CREDIT_CARD",
                                        ]
                                          .map((key) => ({
                                            value: key,
                                            label: creditTypeLocalization.find(
                                              (v) => v.key === key
                                            ).value,
                                          }))
                                          .map(({ label, value }, index) => (
                                            <option key={index} value={value}>
                                              {label}
                                            </option>
                                          ))}
                                      </FastField>
                                    </Layout.Item>
                                    <Layout.Item
                                      default="6/12"
                                      m="1/2"
                                      xs="1/1"
                                    >
                                      <FastField
                                        name={names.creditMonthlyRate}
                                        normalizeOnChange={false}
                                        normalize={normalizeEuroAmount}
                                        reversed
                                        {...currencyAddon}
                                        label={Output.resolve(
                                          "pages.applicationForm.panels.expenditure.fields.credits.monthlyRate.label"
                                        )}
                                        component={TextField}
                                      />
                                    </Layout.Item>
                                  </Layout>
                                </Fieldset.Row>
                                <Conditional
                                  collapsed={
                                    !["CONSUMER_CREDIT", "LEASING"].includes(
                                      c.creditType
                                    )
                                  }
                                  onCollapse={() => {
                                    resetField(names.creditBegin, form);
                                    resetField(names.creditTerm, form);
                                  }}
                                >
                                  <Fieldset.Row>
                                    <Fieldset.Row>
                                      <Layout>
                                        <Layout.Item
                                          default="6/12"
                                          m="1/2"
                                          xs="1/1"
                                        >
                                          <FastField
                                            name={names.creditBegin}
                                            label={Output.resolve(
                                              "pages.applicationForm.panels.expenditure.fields.credits.beginDate.label"
                                            )}
                                            component={DateField}
                                          />
                                        </Layout.Item>
                                        <Layout.Item
                                          default="6/12"
                                          m="1/2"
                                          xs="1/1"
                                        >
                                          <FastField
                                            name={names.creditTerm}
                                            label={Output.resolve(
                                              "pages.applicationForm.panels.expenditure.fields.credits.term.label"
                                            )}
                                            component={SelectField}
                                          >
                                            {toOptions(
                                              Output.resolve(
                                                "pages.applicationForm.panels.expenditure.fields.credits.term.values",
                                                []
                                              )
                                            )}
                                          </FastField>
                                        </Layout.Item>
                                      </Layout>
                                    </Fieldset.Row>{" "}
                                  </Fieldset.Row>
                                </Conditional>
                                <Fieldset.Row>
                                  <Fieldset.Row>
                                    <Layout>
                                      <Layout.Item
                                        default="6/12"
                                        m="1/2"
                                        xs="1/1"
                                      >
                                        {index > 0 && (
                                          <Button
                                            small
                                            link
                                            icon="minus"
                                            iconReversed={true}
                                            onClick={() => {
                                              arrayHelpers.remove(index);
                                            }}
                                          >
                                            {Output.resolve(
                                              "pages.applicationForm.panels.expenditure.fields.credits.buttonRemove.label"
                                            )}
                                          </Button>
                                        )}
                                      </Layout.Item>
                                    </Layout>
                                  </Fieldset.Row>{" "}
                                </Fieldset.Row>
                              </React.Fragment>
                            );
                          })}
                          <Fieldset.Row>
                            <Layout>
                              <Layout.Item default="6/12" m="1/2" xs="1/1">
                                {credits.length < 5 && (
                                  <Button
                                    small
                                    link
                                    icon="plus"
                                    iconReversed={true}
                                    onClick={() => {
                                      if (credits.length < 5) {
                                        arrayHelpers.push(createCredit());
                                      }
                                    }}
                                  >
                                    {Output.resolve(
                                      "pages.applicationForm.panels.expenditure.fields.credits.buttonAdd.label"
                                    )}
                                  </Button>
                                )}
                              </Layout.Item>
                            </Layout>
                          </Fieldset.Row>{" "}
                        </>
                      );
                    }}
                  />
                </Conditional>
              );
            }}
          />
        </Fieldset>
        <Fieldset>
          <Fieldset.Row>{buttons}</Fieldset.Row>
        </Fieldset>
      </Form>
    </FormSection2>
  );
}

ExpenditurePanel.defaultProps = {
  validate: Validation.select(
    "netIncome",
    "additionalNetIncome",
    "livingConditions",
    "selfUsedProperty",
    "monthlyRateLease",
    "monthlyRateProperty",
    "extraCost",
    "alimentChildrenCount",
    "monthlyAlignmentFees",
    "activeCreditContracts",
    "insuranceType",
    "otherObligationAlignment",
    "monthlyFeeInsurance",
    "credits"
  ),
};

export default ExpenditurePanel;
